import { getSceneState } from "../stores/sceneStore";
import { usePhysicsStore } from "../stores/physicsStore";
import {
  buildCurrentSceneInitialJointMap,
  getInitialFreeJointPoseFromScene,
} from "../stores/physics/physicsStartupHelpers";
import { buildBodyExportNames } from "../stores/fileStore/xmlGenerationExportMaps";
import type { BodyUUID, Quaternion, Vector3 } from "../types/scene";
import { logger } from "../utils/logger";
import { prepareCurrentPhysicsWorkerInput } from "./mujocoBridge";
import { getThreadedWasmSupport } from "./threadedWasmSupport";

type WorkerMassPropertiesRow = {
  bodyId: number;
  name: string;
  mass: number;
  subtreeMass: number;
  localCom: Vector3;
  inertialQuat: Quaternion;
  worldCom: Vector3;
  subtreeCom: Vector3;
  diagInertia: Vector3;
};

type WorkerMassPropertiesPayload = {
  totalMass: number;
  totalCom: Vector3;
  bodyCount: number;
  rows: WorkerMassPropertiesRow[];
};

export type MassPropertiesRow = WorkerMassPropertiesRow & {
  bodyUuid: BodyUUID;
  displayName: string;
};

export type MassPropertiesResult = {
  totalMass: number;
  totalCom: Vector3;
  bodyCount: number;
  rows: MassPropertiesRow[];
  computedAt: number;
};

const MASS_PROPERTIES_TIMEOUT_MS = 30000;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const coerceBodyUuidFromMujocoName = (name: string): BodyUUID | null => {
  const raw = String(name || "").trim();
  if (!raw || raw === "world") return null;
  const maybe = raw.startsWith("b_") ? raw.slice(2) : raw;
  return UUID_RE.test(maybe) ? (maybe as BodyUUID) : null;
};

const mapMassPropertiesRows = (payload: WorkerMassPropertiesPayload): MassPropertiesRow[] => {
  const scene = getSceneState();
  const bodies = scene.bodies || {};
  const bodyUuidByXmlName = new Map<string, BodyUUID>();
  const addNameMap = (names: Record<string, string>) => {
    Object.entries(names).forEach(([bodyUuid, xmlName]) => {
      if (xmlName) bodyUuidByXmlName.set(xmlName, bodyUuid as BodyUUID);
    });
  };
  addNameMap(buildBodyExportNames({ bodiesMap: bodies, xmlNamingMode: "uuid" }));
  addNameMap(buildBodyExportNames({ bodiesMap: bodies, xmlNamingMode: "readable" }));

  return (payload.rows || [])
    .map((row) => {
      const bodyUuid = coerceBodyUuidFromMujocoName(row.name) || bodyUuidByXmlName.get(row.name);
      if (!bodyUuid || !bodies[bodyUuid]) return null;
      return {
        ...row,
        bodyUuid,
        displayName: bodies[bodyUuid]?.name || row.name,
      };
    })
    .filter((row): row is MassPropertiesRow => row !== null);
};

export async function computeMassPropertiesFromCurrentScene(): Promise<MassPropertiesResult> {
  const threadedWasmSupport = getThreadedWasmSupport();
  if (!threadedWasmSupport.ok) {
    throw new Error(threadedWasmSupport.message);
  }
  if (usePhysicsStore.getState().isRunning || usePhysicsStore.getState().isPaused) {
    throw new Error("Mass properties cannot be computed while physics is running.");
  }

  const startupPosePolicy = "currentScenePose";
  const sceneState = getSceneState();
  const { xmlString, assets, bodyNamesHint, loadMode } = await prepareCurrentPhysicsWorkerInput({
    startupPosePolicy,
  });

  return await new Promise<MassPropertiesResult>((resolve, reject) => {
    const worker = new Worker(new URL("./mujocoWorker.worker.js", import.meta.url), {
      type: "module",
      credentials: "same-origin",
    });
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      try {
        worker.terminate();
      } catch (error) {
        logger.warn("[mass-properties] Failed to terminate timed-out worker", error);
      }
      reject(new Error("Timed out while computing mass properties."));
    }, MASS_PROPERTIES_TIMEOUT_MS);

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      try {
        worker.terminate();
      } catch (error) {
        logger.warn("[mass-properties] Failed to terminate worker", error);
      }
      callback();
    };

    worker.onerror = (event) => {
      finish(() => reject(new Error(event.message || "Mass properties worker failed.")));
    };
    worker.onmessageerror = () => {
      finish(() => reject(new Error("Mass properties worker message could not be deserialized.")));
    };
    worker.onmessage = (event) => {
      const message = event.data || {};
      if (!message?.type) return;
      if (message.type === "worker-loaded") {
        worker.postMessage({ type: "hello" });
        return;
      }
      if (message.type === "ready") {
        worker.postMessage({
          type: "init",
          payload: {
            xmlString,
            assets,
            timestep: usePhysicsStore.getState().timestep,
            realtimeScale: usePhysicsStore.getState().realtimeScale,
            solverIterations: usePhysicsStore.getState().solverIterations,
            solverTolerance: usePhysicsStore.getState().solverTolerance,
            loadMode,
            bodyNamesHint,
            analysisOnly: true,
            initialJointMap: buildCurrentSceneInitialJointMap(xmlString, sceneState),
            initialFreeJointPose: getInitialFreeJointPoseFromScene(sceneState),
            initialControls: null,
            startupPosePolicy,
            preserveActuatorControls: false,
          },
        });
        return;
      }
      if (message.type === "massProperties") {
        const payload = message.payload as WorkerMassPropertiesPayload;
        finish(() =>
          resolve({
            totalMass: Number(payload?.totalMass) || 0,
            totalCom: payload?.totalCom || [0, 0, 0],
            bodyCount: Number(payload?.bodyCount) || 0,
            rows: mapMassPropertiesRows(payload),
            computedAt: Date.now(),
          })
        );
        return;
      }
      if (message.type === "error") {
        const errorMessage = String(message.payload?.error || message.error || "Worker error");
        finish(() => reject(new Error(errorMessage)));
      }
    };
  });
}
